import { trpc } from '../../trpc/react';
import { useMemo } from 'react';

export type UseExerciseBenchmarksArgs = {
  libraryExerciseIds: string[];
  trackId?: string;
  profileId?: string;
};

/**
 * Loads exercise max conversions and athlete exercise maxes.
 * Pass `trackId` or `profileId` to fetch benchmarks for that scope.
 */
export const useExerciseBenchmarks = (args: UseExerciseBenchmarksArgs) => {
  const { libraryExerciseIds, trackId, profileId } = args;

  const uniqueLibraryExerciseIds = Array.from(new Set(libraryExerciseIds));

  const conversionsEnabled = uniqueLibraryExerciseIds.length > 0;

  const allExerciseMaxConversions =
    trpc.exerciseMaxConversionLibrary.getMaxConversionsForExercises.useQuery(
      { libraryExerciseIds: uniqueLibraryExerciseIds },
      {
        staleTime: 1000 * 60,
        enabled: conversionsEnabled,
      },
    );

  const allExerciseIds = useMemo(() => {
    const ids = new Set(uniqueLibraryExerciseIds);
    allExerciseMaxConversions.data?.forEach(({ fromLibraryExerciseId }) => {
      ids.add(fromLibraryExerciseId);
    });
    return Array.from(ids);
  }, [uniqueLibraryExerciseIds, allExerciseMaxConversions.data]);

  const hasIdentifier = !!trackId || !!profileId;

  const benchmarkInput = trackId
    ? { trackId, libraryExerciseIds: allExerciseIds }
    : { profileId: profileId ?? '', libraryExerciseIds: allExerciseIds };

  const benchmarks = trpc.athleteProfiles.getExerciseBenchmarks.useQuery(
    benchmarkInput,
    {
      staleTime: 1000 * 60,
      enabled: hasIdentifier && allExerciseIds.length > 0,
    },
  );

  const conversions = allExerciseMaxConversions.data ?? [];

  const isLoading =
    (conversionsEnabled && allExerciseMaxConversions.isLoading) ||
    benchmarks.isLoading;

  return {
    isLoading,
    exerciseBenchmarks: benchmarks.data ?? [],
    conversions,
  };
};
